import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { DialogContent } from '@reach/dialog';
import FocusLock from 'react-focus-lock';
import { hideOthers } from 'aria-hidden';
import { __RouterContext } from 'react-router';
import styled, { css } from 'styled-components';
import { useRouteChange } from '@ally/use-route-change';
import { Box } from '../Box';
import { CloseButton, CloseButtonSpacer } from '../tools/components';
import { ModalContext, useModalContext, useModal, } from './ModalContext';
import { useModalObserver } from './useModalObserver';
import { getContentWidth, getContentMaxWidth } from './utils';
import { a11y } from './strings';
import { upToSm } from '../tools/utils';
const fullScreenStyles = css `
  width: 100vw;
  height: 100%;
  margin: 0;
  overflow-y: scroll;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
`;
const StyledDialogContent = styled(DialogContent) `
  ${({ theme: { media, colors, space }, $fullScreenSmDown, $fullScreenMdOnly, $height, $width = 'lg', }) => `
      position: relative;
      background: ${colors.white};
      height: ${$height || 'fit-content'};
      box-sizing: border-box;
      max-width: 100vw;
      margin: ${space.lg} 0;
      outline: none;
      overflow: hidden;
      border-radius: 4px;
      box-shadow: 0 2px 4px -2px rgba(0,0,0,0.16), 0 5px 8px -4px rgba(0,0,0,0.16); 

      ${media.down('sm')} {
        width: ${getContentWidth({
    modalWidth: $width,
    screenWidth: 'sm-down',
})};
        ${$fullScreenSmDown ? `${fullScreenStyles}` : ''}
      }

      ${media.between('md', 'md')} {
        width: ${getContentWidth({
    modalWidth: $width,
    screenWidth: 'md-only',
})};
        ${$fullScreenMdOnly ? `${fullScreenStyles}` : ''}
      }

      ${media.up('lg')} {
        width: ${getContentWidth({ modalWidth: $width, screenWidth: 'lg-up' })};
        max-width: ${getContentMaxWidth($width)};
      }
  `}
`;
const StyledBox = styled(Box) `
  ${({ theme: { media }, $fullScreenSmDown, $fullScreenMdOnly }) => `
    ${media.down('sm')} {
      ${$fullScreenSmDown ? `flex: 1;` : ''}
    }

    ${media.between('md', 'md')} {
      ${$fullScreenMdOnly ? `flex: 1;` : ''}
    }
  `}
`;
const useLocation = () => {
    const values = React.useContext(__RouterContext);
    return values === null || values === void 0 ? void 0 : values.location;
};
const StyledFocusLock = styled(FocusLock)(({ theme: { media }, $fullScreenSmDown, $fullScreenMdOnly }) => css `
    ${media.down('sm')} {
      ${$fullScreenSmDown ? 'height: 100%' : ''}
    }
    ${media.between('md', 'md')} {
      ${$fullScreenMdOnly ? 'height: 100%' : ''}
    }
  `);
const StyledOverlay = styled.div `
  ${({ $fullScreenSmDown, $fullScreenMdOnly, $isIntersectingViewport, theme: { media }, }) => `
    @media print {
      position: static;
    }
    background: hsla(0, 0%, 0%, 0.3);
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    height: 100%;
    overflow: auto;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: ${$isIntersectingViewport ? 'flex-start' : 'center'};

    ${media.down('sm')} {
      ${$fullScreenSmDown ? 'align-items: flex-start;' : ''} 
    }

    ${media.between('md', 'md')} {
      ${$fullScreenMdOnly ? 'align-items: flex-start;' : ''} 
    }
  `}
`;
const Overlay = ({ onDismiss, $fullScreenSmDown, $fullScreenMdOnly, $isIntersectingViewport, children, disableDismiss, }) => {
    const overlayRef = useRef(null);
    const mouseDownTargetRef = useRef(false);
    const mouseUpTargetRef = useRef(false);
    const { show, closeModal } = useModal();
    const handleOnDismiss = () => {
        if (!disableDismiss) {
            closeModal();
            onDismiss();
        }
    };
    if (show) {
        return (React.createElement(StyledOverlay, { ref: (r) => {
                overlayRef.current = r;
            }, 
            // We compare the ref to the target to make sure the click event
            // originated in the overlay and wasn't bubbled up from an element inside modal
            onMouseDown: (e) => {
                if (e.target === overlayRef.current) {
                    mouseDownTargetRef.current = true;
                }
            }, onMouseUp: (e) => {
                if (e.target === overlayRef.current && mouseDownTargetRef.current) {
                    mouseUpTargetRef.current = true;
                }
                else {
                    mouseDownTargetRef.current = false;
                }
            }, onClick: (e) => {
                // Checks that a click starts and ends on the overlay and closes the modal if it has.
                if (mouseDownTargetRef.current &&
                    mouseUpTargetRef.current &&
                    e.target === overlayRef.current) {
                    handleOnDismiss();
                }
                mouseDownTargetRef.current = false;
                mouseDownTargetRef.current = false;
            }, "$fullScreenSmDown": $fullScreenSmDown, "$fullScreenMdOnly": $fullScreenMdOnly, "$isIntersectingViewport": $isIntersectingViewport, "data-testid": "modal-overlay" }, children));
    }
    return null;
};
const DialogContentWrapper = ({ content, disableDismiss = false, focusRef, disableFullScreenSmDown, fullScreenMd, height, width, hideClose, onDismissCallback, dataTrackRegion, toggleCallback = (isOpen) => {
    /* noop */
}, disableReturnFocus, className, ariaLabelCloseButton, }) => {
    const { show, showModal, closeModal, originallyFocusedElement } = useModal();
    const ContentElement = React.cloneElement(content, {
        showModal,
        closeModal,
        show,
    });
    const { dialogContentRef, isIntersectingViewport } = useModalObserver({
        isModalShowing: show,
    });
    const [dialogRef, setDialogRef] = React.useState(null);
    React.useEffect(() => {
        let undo;
        if (show && dialogRef) {
            undo = hideOthers(dialogRef);
        }
        return () => {
            if (undo) {
                undo();
            }
        };
    }, [show, dialogRef]);
    const onActivation = React.useCallback((el) => {
        var _a, _b;
        setDialogRef(el);
        if (typeof focusRef === 'object') {
            ;
            (_b = (_a = focusRef === null || focusRef === void 0 ? void 0 : focusRef.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
        if (typeof focusRef === 'function') {
            focusRef(el);
        }
    }, [focusRef]);
    const onDeactivation = React.useCallback(() => {
        setDialogRef(null);
        if (!disableReturnFocus &&
            originallyFocusedElement instanceof HTMLElement) {
            originallyFocusedElement.focus();
        }
    }, [originallyFocusedElement]);
    const onDismiss = React.useCallback(() => {
        if (!disableDismiss) {
            closeModal();
            toggleCallback(false);
        }
    }, [disableDismiss, closeModal, toggleCallback]);
    const onClickCloseButton = React.useCallback(() => {
        closeModal();
        toggleCallback(false);
        if (onDismissCallback) {
            onDismissCallback();
        }
    }, [closeModal, toggleCallback, onDismissCallback]);
    return (React.createElement(Overlay, { "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, "$isIntersectingViewport": isIntersectingViewport, onDismiss: onDismiss, disableDismiss: disableDismiss },
        React.createElement(StyledFocusLock, { "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, onActivation: onActivation, onDeactivation: onDeactivation, hasPositiveIndices: false },
            React.createElement(StyledDialogContent, { ref: dialogContentRef, "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, "$height": height, "$width": width, role: "dialog", "data-track-region": dataTrackRegion, className: className, "data-testid": "mui-modal-container" },
                !hideClose && (React.createElement(CloseButtonSpacer, { isFullScreenSmDown: !disableFullScreenSmDown })),
                React.createElement(StyledBox, { p: upToSm('md', 'lg'), "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd }, ContentElement),
                !hideClose && (React.createElement(CloseButton, { "data-testid": "close-modal", "data-track-elem": "icon", "data-track-name": "Close", "data-track-trigger": "close", ariaLabel: ariaLabelCloseButton, onClick: onClickCloseButton }))))));
};
const usePortalWrapper = () => {
    const [portalWrapper, setPortalWrapper] = React.useState(null);
    React.useEffect(() => {
        const newPortalWrapper = document.createElement('div');
        document.body.appendChild(newPortalWrapper);
        setPortalWrapper(newPortalWrapper);
        return () => newPortalWrapper.remove();
    }, []);
    return portalWrapper;
};
const Modal = React.forwardRef((props, ref) => {
    const { content, disableDismiss = false, disableFullScreenSmDown, fullScreenMd, height, width, hideClose, manualToggle, setShowOption, showOnLoad, toggleCallback = (isOpen) => {
        /* noop */
    }, trigger = null, disableCloseOnRouteChange, onDismissCallback, disableReturnFocus, className, context, ariaLabelCloseButton = a11y.ariaLabelCloseButton, } = props;
    const defaultContext = useModalContext({ initialShow: showOnLoad });
    const safeContext = context || defaultContext;
    const { show, setShow } = safeContext;
    const currentRoute = useLocation();
    useRouteChange((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.pathname) || '', () => {
        if (currentRoute && !disableCloseOnRouteChange) {
            setShow(false);
        }
    });
    React.useEffect(() => {
        if (manualToggle)
            setShow(!!setShowOption);
    }, [manualToggle, setShowOption, setShow]);
    React.useEffect(() => {
        const htmlStyle = document.documentElement.style;
        const bodyStyle = document.body.style;
        if (show) {
            htmlStyle.overflow = 'hidden';
            bodyStyle.overflow = 'hidden';
        }
        else {
            htmlStyle.overflow = 'auto';
            bodyStyle.overflow = 'auto';
        }
    }, [show]);
    const portalWrapper = usePortalWrapper();
    return (React.createElement(ModalContext.Provider, { value: safeContext },
        trigger &&
            React.cloneElement(trigger, {
                openModal: () => {
                    setShow(true);
                    toggleCallback(true);
                },
            }),
        portalWrapper &&
            createPortal(React.createElement(DialogContentWrapper, { content: content, disableDismiss: disableDismiss, focusRef: ref, disableFullScreenSmDown: disableFullScreenSmDown, fullScreenMd: fullScreenMd, height: height, width: width, hideClose: hideClose, toggleCallback: toggleCallback, onDismissCallback: onDismissCallback, disableReturnFocus: disableReturnFocus, className: className, ariaLabelCloseButton: ariaLabelCloseButton }), portalWrapper)));
});
const ModalForProps = () => React.createElement(React.Fragment, null);
export { Modal, ModalForProps };
//# sourceMappingURL=Modal.js.map